import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTenantData } from '../context/TenantDataContext';
import { syncTenant, listOrganizations } from '../api';
import NcIcon from './NcIcon';
import Logo from './Logo';
import SyncStatusModal from './SyncStatusModal';
import SidebarUserMenu from './SidebarUserMenu';
import { useItList } from '../hooks/useItList';
import { useLicense } from '../hooks/useLicense';
import { licenseSummary } from '../utils/license';
import { useSyncStatus } from '../utils/freshness';
import './Sidebar.css';

// Grouping matches the approved redesign exactly. Gæstebrugere, Grupper &
// Medlemskab, Intune Apps, Copilot & AI and Historik aren't top-level items
// in the new IA — their pages and routes still exist and still work
// (nothing is deleted), they're just reached from relations/links on other
// pages now instead of the sidebar, same as the new IA's own examples treat
// "Gæstebrugere" as a group inside Mennesker & login rather than its own
// nav entry.
// IA per the V7 spec: Sikkerhedsstatus and "Det kræver handling" are folded
// into Mit overblik (no separate page in the nav), Databeskyttelse and
// Samarbejde (Teams) aren't in V7's top-level list — their routes still
// work, they're just reached via relations/links now, same treatment as
// Gæstebrugere/Grupper/Intune Apps/Copilot already got in the prior pass.
// The two daily-driver pages sit ungrouped up top; every per-area page
// (Mennesker & login, Enheder, Applikationer, ...) is reached through
// "Detaljeret visning"'s own horizontal area switcher instead of its own
// sidebar entry — their routes still exist unchanged (object-detail
// overlays still link back to them), they're just not top-level nav items
// any more. Dokumentation is untouched.
const NAV = [
  { type: 'link', key: '', icon: 'grid', label: 'Overblik', hint: 'Hvordan står vi lige nu?' },
  { type: 'link', key: 'work-queue', icon: 'box', label: 'Gennemgang', sub: true, hint: 'Det, Microsoft 365 har fundet, og som I skal tage stilling til.' },
  // Politikker was split in two: Konfiguration ("hvordan er det sat op?") and Compliance
  // ("hvilke krav er opfyldt?") — they were doing three different jobs on one page before.
  { type: 'link', key: 'configuration', icon: 'layers', label: 'Konfiguration', hint: 'Sådan er jeres Microsoft 365 sat op i dag.' },
  { type: 'link', key: 'compliance', icon: 'shield', label: 'Compliance', hint: 'Lever I op til kravene i ISO 27001, NIS2 og GDPR?' },

  { type: 'divider' },
  // `premium`: part of the Premium licence — shown with a lock (and a "Premium" tag on the group label)
  // when the tenant's licence doesn't include documentation. The page itself explains what's missing.
  { type: 'label', label: 'Dokumentation' },
  // Revisionsspor holds three views (Beslutninger, IT-opgaver, Historik), each its own route; the entry is active on all three.
  { type: 'link', key: 'decisions', icon: 'info', label: 'Revisionsspor', hint: 'Beslutninger, IT-opgaver og historik samlet.', trail: true },
  { type: 'link', key: 'audit', icon: 'clipboard', label: 'Rapporter', hint: 'Færdige rapporter til ledelse og revisor.', premium: true },
];

function LockIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="5" y="11" width="14" height="10" rx="2" />
      <path d="M8 11V7a4 4 0 018 0v4" />
    </svg>
  );
}

export default function Sidebar({ tenantId, allTenants, narrow = false }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { role } = useAuth();
  const { tenant, refetch } = useTenantData();
  const license = useLicense();
  const docLocked = !license.features.documentation;
  const [switcherOpen, setSwitcherOpen] = useState(false);
  const [syncStatusOpen, setSyncStatusOpen] = useState(false);
  // Only the user folds the menu to icons. A page with its own second menu (the narrow prop) just makes this one
  // narrower, with the text kept.
  const [collapsed, setCollapsed] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const isAdmin = role === 'admin';
  const backToPortalPath = role === 'provider' ? '/organizations' : role === 'admin' ? '/tenants' : null;
  // The organisation this tenant belongs to, for the breadcrumb
  // (Organisationer › Dany) — providers only.
  const [org, setOrg] = useState(null);
  useEffect(() => {
    if (role !== 'provider' || !tenant?.orgId) return undefined;
    let cancelled = false;
    listOrganizations()
      .then((d) => !cancelled && setOrg((d.organizations || []).find((o) => o.id === tenant.orgId) || null))
      .catch(() => {});
    return () => { cancelled = true; };
  }, [role, tenant?.orgId]);

  // A person may ask for a fresh read from Microsoft at most every four hours per customer; the backend keeps that cooldown and tells
  // when the next one may start, so the button says so instead of failing.
  const syncStatus = useSyncStatus(tenant?.tenantId, { poll: false });
  const [nextSyncAt, setNextSyncAt] = useState(null);
  const waitUntil = nextSyncAt || syncStatus.nextManualSyncAt;
  // In the customer portal everybody is held to the four hours, administrators too: it behaves as it does for the customer.
  const syncWaiting = !!waitUntil && Date.parse(waitUntil) > Date.now();
  const clockOf = (iso) => (iso ? new Date(iso).toLocaleTimeString('da-DK', { hour: '2-digit', minute: '2-digit' }) : '');
  async function handleSync() {
    if (!tenant?.tenantId || syncing || syncWaiting) return;
    setSyncing(true);
    try {
      const res = await syncTenant(tenant.tenantId, { force: true });
      if (res?.nextAllowedAt) setNextSyncAt(res.nextAllowedAt);
      if (res && !res.skipped) await refetch();
      syncStatus.reload();
    } finally {
      setSyncing(false);
    }
  }

  const basePath = `/tenant/${tenantId}`;
  const itList = useItList(tenantId);
  // When an object-detail drawer is open, the real browser location is the
  // detail route (e.g. .../identity/:userId), but the list underneath it
  // is what's actually still on screen — checking the background location
  // when there is one keeps that list's sidebar entry highlighted instead
  // of going dark while the drawer is open.
  const activePathname = location.state?.backgroundLocation?.pathname || location.pathname;
  // Each area page's own route (identity, devices, ...) still exists —
  // an object-detail overlay's backTo still points there, and the Work
  // Queue's own "Alle objekter" mode mounts them directly — they just
  // aren't their own sidebar entry any more. Landing on one directly
  // should still highlight the Work Queue instead of nothing being active.
  const legacyAreaKeys = ['identity', 'devices', 'security', 'exchange', 'sharepoint', 'applications', 'roles', 'conditional-access', 'licenses', 'detailed'];
  const isOnLegacyArea = legacyAreaKeys.some((key) => activePathname === `${basePath}/${key}`);
  const isActive = (path) => {
    if (path === 'work-queue' && isOnLegacyArea) return true;
    if (path === 'decisions') return ['decisions', 'it-list', 'history'].some((p) => activePathname === `${basePath}/${p}`);
    return activePathname === (path ? `${basePath}/${path}` : basePath);
  };

  return (
    <aside className={`sidebar${collapsed ? ' sidebar--collapsed' : narrow ? ' sidebar--narrow' : ''}${narrow ? ' sidebar--with-menu' : ''}`}>
      <div className="sidebar__brand-row">
        <div className="sidebar__brand" onClick={() => navigate(isAdmin ? '/tenants' : basePath)}>
          <Logo size={29} className="sidebar__brand-icon" />
          <div className="sidebar__brand-divider" />
          <div className="sidebar__brand-stack sidebar__collapsible-text">
            <span className="sidebar__brand-text">nord<span>comply</span></span>
            <span className="sidebar__portal-badge">Customer Portal</span>
          </div>
        </div>
        <button
          type="button"
          className="sidebar__collapse-btn"
          onClick={() => setCollapsed((c) => !c)}
          title={collapsed ? 'Fold sidemenu ud' : 'Fold sidemenu sammen'}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M15 5l-7 7 7 7" /></svg>
        </button>
      </div>

      {backToPortalPath && (
        <nav className="sidebar__crumbs" aria-label="Brødsmulesti">
          <button type="button" className="sidebar__crumb" title={role === 'provider' ? 'Til organisationer' : 'Til tenants'} onClick={() => navigate(backToPortalPath)}>
            ← <span className="sidebar__collapsible-text">{role === 'provider' ? 'Organisationer' : 'Tenants'}</span>
          </button>
          {role === 'provider' && org && (
            <span className="sidebar__collapsible-text sidebar__crumbs-rest">
              <span aria-hidden="true">›</span>
              <button type="button" className="sidebar__crumb" onClick={() => navigate(`/tenants?org=${org.id}&orgName=${encodeURIComponent(org.name)}`)}>{org.name}</button>
            </span>
          )}
        </nav>
      )}

      {isAdmin ? (
        <div className="tenant-switch" onClick={() => setSwitcherOpen((o) => !o)} title={tenant?.name}>
          <div>
            <div className="tenant-switch__name">{tenant?.name}</div>
            {allTenants?.length > 1 && (
              <div className="tenant-switch__domain sidebar__collapsible-text">
                {allTenants.length} tenants — klik for at skifte
              </div>
            )}
          </div>
          <span className="sidebar__collapsible-text"><NcIcon name="chevronDown" size={14} /></span>
          {switcherOpen && allTenants?.length > 0 && (
            <div className="tenant-switch__menu" onClick={(e) => e.stopPropagation()}>
              {allTenants.map((t) => (
                <button
                  key={t.id}
                  className="tenant-switch__option"
                  onClick={() => {
                    setSwitcherOpen(false);
                    navigate(`/tenant/${t.id}`);
                  }}
                >
                  {t.name}
                </button>
              ))}
            </div>
          )}
        </div>
      ) : (
        <div className="tenant-switch tenant-switch--static" title={tenant?.name}>
          <div>
            <div className="tenant-switch__name">{tenant?.name}</div>
          </div>
        </div>
      )}

      {license.tier === 'trial' && !license.expired && (
        <div className="sidebar__trial sidebar__collapsible-text" title="Prøveperiode">{licenseSummary(license)}</div>
      )}

      <nav className="sidebar__nav">
        {NAV.map((item, i) => {
          if (item.type === 'label') {
            return (
              <div className="sidebar__group-label" key={item.label}>
                {item.label}
                {item.premium && docLocked && <span className="sidebar__tier-pill sidebar__collapsible-text">Premium</span>}
              </div>
            );
          }

          if (item.type === 'divider') {
            return <div className="sidebar__divider" key={`divider-${i}`} />;
          }

          const locked = item.premium && docLocked;
          return (
            <button
              key={item.key}
              className={`sidebar__link ${isActive(item.key) ? 'is-active' : ''}${locked ? ' is-locked' : ''}`}
              onClick={() => navigate(item.key ? `${basePath}/${item.trail && docLocked ? 'history' : item.key}` : basePath)}
              title={locked ? `${item.label} (kræver Premium). ${item.hint}` : `${item.label}: ${item.hint}`}
            >
              <NcIcon name={item.icon} size={16} />
              <span className="sidebar__link-label">{item.label}</span>
              {item.trail && itList.waiting.length > 0 && <span className="sidebar__badge" title="Punkter på IT-opgaver">{itList.waiting.length}</span>}
              {locked && <span className="sidebar__lock sidebar__collapsible-text"><LockIcon /></span>}
            </button>
          );
        })}
      </nav>

      <div style={{ flex: 1 }} />
      <SidebarUserMenu tenant={tenant} onOpenSyncStatus={() => setSyncStatusOpen(true)} onSync={handleSync} syncing={syncing} syncNote={syncWaiting ? `Næste synkronisering kl. ${clockOf(waitUntil)}` : (syncStatus.lastSyncAt ? `Sidst synkroniseret kl. ${clockOf(syncStatus.lastSyncAt)}` : null)} syncDisabled={syncWaiting} />

      {syncStatusOpen && tenantId && <SyncStatusModal tenantId={tenantId} onClose={() => setSyncStatusOpen(false)} />}
    </aside>
  );
}
